import type { IssuePersonSummary, IssueVehicleSummary } from '../issue/issue.interface';
import type { InspectionSource, InspectionStatus, ScheduledInterval } from './inspection.interface';

/** The Inspections screen tabs, in display order (SWOOP-400). A tab is a status. */
export type InspectionTab = 'due' | 'inProgress' | 'completed';

/** The longest vehicle search the inspections list accepts (SWOOP-401): well above any plate, fleet ID, make or model. */
export const INSPECTION_SEARCH_MAX_LENGTH = 100;

/**
 * Body of POST /inspections/stats: the master selector's narrowing, or the page's Location filter in `locationId`
 * (SWOOP-401), plus the page's vehicle search.
 */
export interface InspectionStatsCriteria {
  locationId?: string;
  territoryId?: string;
  /** Vehicle text: plate (spaces and dashes ignored), fleet ID, make label or model; partial, case-insensitive. */
  search?: string;
}

/** Body of POST /inspections/search: the stats criteria plus the tab, as `status`. */
export interface SearchInspectionsCriteria extends InspectionStatsCriteria {
  status: InspectionStatus;
}

/** The vehicle cell: the issue summary plus whether the vehicle is archived (FR-014). */
export interface InspectionVehicleSummary extends IssueVehicleSummary {
  archived: boolean;
}

/**
 * The template cell. (`InspectionTemplateSummary` is taken: the settings card's type,
 * `inspection-template.interface.ts`.)
 */
export interface InspectionListTemplate {
  id: string;
  /** Current name, read including archived templates; `null` only if the row no longer exists at all. */
  name: string | null;
  archived: boolean;
}

/** One row of the Inspections list. Dates are ISO strings on the wire. */
export interface InspectionListItem {
  id: string;
  status: InspectionStatus;
  source: InspectionSource;
  /** The template interval for a scheduled row; `null` on demand. */
  schedule: ScheduledInterval | null;
  /** `null` on demand: the date column reads "On demand". */
  dueOn: Date | null;
  /** Due and before today's business day. Always `false` for other statuses. */
  overdue: boolean;
  startedAt: Date | null;
  completedAt: Date | null;
  /** Who started it, and therefore who is carrying it out or did it; `null` while due. */
  inspector: IssuePersonSummary | null;
  template: InspectionListTemplate;
  vehicle: InspectionVehicleSummary;
}

/** The three tab counts for the caller's scope. The menu badge reads `tabs.due` (FR-012, SC-003). */
export interface InspectionStats {
  tabs: { due: number; inProgress: number; completed: number };
}
